import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authApi } from '@/api/auth.api';
import { useToast } from '@/hooks/use-toast';
import { ResetPasswordCopy, ResetPasswordViewState } from './reset-password.types';

type UseResetPasswordFormOptions = {
  t: ResetPasswordCopy;
};

const STRONG_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{8,}$/;
const SPECIAL_CHARACTER_PATTERN = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

export function useResetPasswordForm({ t }: UseResetPasswordFormOptions) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { lang, token } = useParams<{ lang: string; token: string }>();
  const currentLang = lang || 'fr';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const passwordHasMinLength = password.length >= 8;
  const passwordHasLetterCase = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const passwordHasDigitAndSpecial = /\d/.test(password) && SPECIAL_CHARACTER_PATTERN.test(password);
  const passwordIsStrong = STRONG_PASSWORD_PATTERN.test(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = passwordIsStrong && passwordsMatch && Boolean(token) && !loading;

  const viewState = useMemo<ResetPasswordViewState>(() => {
    if (verifying) return 'verifying';
    if (!tokenValid) return 'invalid';
    if (resetSuccess) return 'success';
    return 'form';
  }, [resetSuccess, tokenValid, verifying]);

  useEffect(() => {
    let cancelled = false;

    const verifyToken = async () => {
      if (!token) {
        if (!cancelled) {
          setTokenValid(false);
          setVerifying(false);
        }
        return;
      }

      try {
        const result = await authApi.verifyResetToken(token);
        if (!cancelled) setTokenValid(result.valid);
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };

    verifyToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const goToLogin = () => navigate(`/${currentLang}/login`);
  const requestNewLink = () => navigate(`/${currentLang}/forgot-password`);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!passwordIsStrong) {
      toast({
        title: t.resetPasswordTooShort,
        description: t.resetPasswordTooShortDesc,
        variant: 'destructive',
      });
      return;
    }

    if (!passwordsMatch) {
      toast({
        title: t.resetPasswordMismatch,
        description: t.resetPasswordMismatchDesc,
        variant: 'destructive',
      });
      return;
    }

    if (!token) {
      toast({
        title: t.resetPasswordMissingToken,
        description: t.resetPasswordMissingTokenDesc,
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.resetPassword(token, {
        password,
        confirmPassword,
      });

      setResetSuccess(true);
      toast({
        title: t.resetPasswordSuccessToast,
        description: response.message || t.resetPasswordSuccessToastDesc,
      });

      window.setTimeout(goToLogin, 2000);
    } catch (error: any) {
      const message = error?.response?.data?.message || t.resetPasswordErrorDesc;
      toast({
        title: t.resetPasswordError,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    currentLang,
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    loading,
    viewState,
    passwordHasMinLength,
    passwordHasLetterCase,
    passwordHasDigitAndSpecial,
    passwordsMatch,
    canSubmit,
    setPassword,
    setConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
    handleSubmit,
    goToLogin,
    requestNewLink,
  };
}
